/**
 * Minimal typed i18n for the admin SPA, same shape as @equinox/shared's
 * translator so the two surfaces behave identically for anyone reading the
 * code. es-MX is the reference catalog and the only one that ships complete;
 * en-US is fully translated per project convention, fr-FR and it-IT are
 * scaffolded. See docs/decisions/2026-07-31-admin-i18n-catalog.md for why
 * this catalog is not simply an addition to @equinox/shared's.
 */

import { SUPPORTED_LOCALES, DEFAULT_LOCALE, type Locale } from '@equinox/shared';
import { adminEsMX } from './es-MX.js';
import { adminEnUS } from './en-US.js';
import { adminFrFR } from './fr-FR.js';
import { adminItIT } from './it-IT.js';

export type AdminMessages = typeof adminEsMX;
export type AdminMessageKey = keyof AdminMessages;

const catalogs: Record<Locale, Partial<AdminMessages>> = {
  'es-MX': adminEsMX,
  'en-US': adminEnUS,
  'fr-FR': adminFrFR,
  'it-IT': adminItIT,
};

export function adminTranslator(locale: Locale = DEFAULT_LOCALE) {
  const primary = catalogs[locale] ?? {};
  return function t(key: AdminMessageKey, vars?: Record<string, string | number>): string {
    const template = (primary[key] ?? adminEsMX[key] ?? key) as string;
    if (!vars) return template;
    return template.replace(/\{(\w+)\}/g, (match, name: string) =>
      name in vars ? String(vars[name]) : match,
    );
  };
}

export type AdminTranslator = ReturnType<typeof adminTranslator>;

export { SUPPORTED_LOCALES, DEFAULT_LOCALE };
export type { Locale };
export { adminEsMX, adminEnUS, adminFrFR, adminItIT };
